import React, { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

const DropdownLiveSearch = ({
    options = [],
    label,
    value,
    onValueChange,
}) => {
    const DEBOUNCE_MS = 180;
    const MIN_CHARS = 1;
    const MAX_RESULTS = 200;

    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [filtered, setFiltered] = useState(() =>
        Array.isArray(options) ? options.slice(0, Math.min(50, options.length)) : []
    );

    const debounceRef = useRef(null);

    const normalizedOptions = useMemo(() => {
        if (!Array.isArray(options)) return [];
        const seen = new Set();
        return options
            .map((o) => (typeof o === "string" ? o : String(o)))
            .filter((s) => seen.has(s) ? false : (seen.add(s), true));
    }, [options]);

    const computeFiltered = (q) => {
        if (!q || q.trim().length < MIN_CHARS) {
            return normalizedOptions.slice(0, Math.min(50, normalizedOptions.length));
        }
        const low = q.toLowerCase();
        const prefix = [];
        const substring = [];

        for (let i = 0; i < normalizedOptions.length; i++) {
            const item = normalizedOptions[i];
            const itemLow = item.toLowerCase();
            if (itemLow.startsWith(low)) {
                prefix.push(item);
                if (prefix.length >= MAX_RESULTS) break;
            } else if (itemLow.includes(low)) {
                substring.push(item);
            }
        }

        return prefix.concat(substring).slice(0, MAX_RESULTS);
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setLoading(true);

        debounceRef.current = setTimeout(() => {
            const q = inputValue == null ? "" : inputValue.trim();
            try {
                const newFiltered = computeFiltered(q);
                setFiltered(newFiltered);
            } finally {
                setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [inputValue, normalizedOptions]);

    useEffect(() => {
        setFiltered(normalizedOptions.slice(0, Math.min(50, normalizedOptions.length)));
        if (value == null) setInputValue("");
    }, [normalizedOptions]);

    useEffect(() => {
        if (value == null) {
            setInputValue("");
        } else {
            setInputValue(String(value));
        }
    }, [value]);

    return (
        <Autocomplete
            options={filtered}
            getOptionLabel={(opt) => (opt ? String(opt) : "")}
            filterOptions={(x) => x}
            freeSolo={false}
            inputValue={inputValue}
            value={value ?? null}
            onInputChange={(_, newInput) => {
                setInputValue(newInput ?? "");
                if (!newInput) {
                    if (typeof onValueChange === "function") onValueChange(null);
                }
            }}
            onChange={(_, newValue) => {
                if (typeof onValueChange === "function") onValueChange(newValue ?? null);
                setInputValue(newValue ? String(newValue) : "");
            }}
            loading={loading}
            clearIcon={null}
            noOptionsText={
                inputValue && inputValue.trim().length >= MIN_CHARS ? "No results" : "Start typing"
            }
            ListboxProps={{
                sx: {
                    fontSize: 14,
                    paddingY: "4px",
                    "& .MuiAutocomplete-option": {
                        padding: "6px 12px",
                        fontSize: 14,
                        "&:hover": {
                            backgroundColor: "#DFDDF3",
                        },
                        '&[aria-selected="true"]': {
                            backgroundColor: "#3E318F",
                        },
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        classes: {
                            root: "custom-input-root-inner",
                            input: "custom-input-element",
                        },
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={18} /> : null}
                                {params.InputProps?.endAdornment}
                            </>
                        ),
                    }}
                    InputLabelProps={{
                        classes: { root: "custom-input-label" },
                    }}
                />
            )}
        />
    );
};

export default DropdownLiveSearch;
